import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { selectIsLoadingProject } from '@ff-os/pdp-shared-redux/slices/projectLoadingSlice';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { useContain } from '@ff-os/shared-react/hooks/useContain';
import { useSize } from '@ff-os/shared-react/hooks/useSize';
import { selectVariablePaintables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { Paintable } from 'ff-canvas';
import { selectMetaViewbox } from '../_redux-meta';
import { selectBackgroundPaintableByVariantId, selectSpacesForFullPreview2d, selectVariantPreviewClip } from '../_redux-variants';
import { Preview2d } from './Preview2d';
import styles from '../GiftPreviewUi.module.scss';

const _GiftPreview2d = ({ paintables, spaces, previewClip, isLoadingProject }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = useSize(containerRef);
    const contentSize = useContain(containerSize, spaces.sceneWidth / spaces.sceneHeight);
    return (
        <div ref={containerRef} className={styles.container}>
            <div style={contentSize} className={styles.preview}>
                {isLoadingProject
                    ? <Spinner overlaySize='full' spinnerSize='medium' />
                    : <Preview2d paintables={paintables} spaces={spaces} previewClip={previewClip} />}
            </div>
        </div>
    );
};

type Props = {
    paintables: Paintable[];
    spaces: Spaces;
    previewClip: Clip | undefined;
    isLoadingProject: boolean;
};

const mapStateToProps = (state: GiftPdpState) => ({
    paintables: selectPaintablesForPreview2d(state, 'mockup'),
    spaces: selectSpacesForFullPreview2d(state),
    previewClip: selectVariantPreviewClip(state),
    isLoadingProject: selectIsLoadingProject(state)
});

export const GiftPreview2d = connect(
    mapStateToProps
)(_GiftPreview2d);

const selectPaintablesForPreview2d = createSelector([
    (state: GiftPdpState, paintTarget: PaintTarget) => selectVariablePaintables(state, paintTarget),
    state => selectPreviewBackgroundPaintableForPreview2d(state)
], (paintables: Paintable[], background: Paintable) => paintables.concat(background));

const selectPreviewBackgroundPaintableForPreview2d = createSelector([
    state => state,
    selectMetaViewbox
], selectBackgroundPaintableByVariantId);